<template>
    <div :class="['row', 'account', isPartnerAdmin ? 'editable' : 'not-editable', currentStatus]" @click="selectAccount">
        <div class="top">
            <div class="item-image">
                <CompanyLogo 
                    :domain="account.domain ? account.domain : null"
                    :company_name="account.name ? account.name : account.domain ? account.domain : null"
                />
            </div>

            <div class="content">
                <div class="account-name">{{ account.name || '-' }}</div>
                <p class="account-url">{{ account.url }}{{ isPartnerGod && account.partner_id ? ' - ' + account.partner_id : '' }}{{ isPartnerAdmin && account.product ? ' - ' + account.product : '' }}{{ isPartnerAdmin && account.paid_to_time ? ' - ' + timestampToDisplayDate(account.paid_to_time) : '' }}</p>
            </div>

            <div class="actions">
                <div class="btn-1 btn-s-icon" v-if="isPartnerAdmin" @click.stop="editAccountPopup(account)">
                    <div class="icon-edit-neg"></div>
                </div>
                <div class="btn-1 btn-s-icon card-action">
                    <div class="icon-arrow-neg"></div>
                </div>
            </div>
        </div>

        <div class="bottom" v-if="(isProspectPro || isDevelopers) && isPartnerAdmin && !isFree">
            <div class="statusses">
                <div class="product" :class="[hasCancelledSubscription ? 'danger' : isGracePeriod ? 'warning' : !isTrial ? 'success' : '']" @click.stop="accountSubscriptions()">
                    <div>{{ product['title_' + locale] ? product['title_' + locale] : product.product || account.product }}{{ isCustom ? ' (partner)' : '' }}</div>
                </div>
                <div v-if="(isProspectPro)" class="status tracker" :class="[!!account?.active_tracker ? 'success' : 'danger']" @click.stop="accountSettings()">
                    <div>{{$t('components.account_list_item.tracker')}}</div>
                </div>
                <div v-if="(isProspectPro)" class="status api-key" :class="[!!account?.api_key_exists ? 'success' : 'danger']" @click.stop="accountSettings()">
                    <div>{{$t('components.account_list_item.api_key')}}</div>
                </div>
            </div>

            <p v-if="isTrial" class="notice">{{$t('components.account_list_item.notices.' + (isGracePeriod ? 'trial_grace' : 'trial'), {days: daysFromNow(account.paid_to_time)})}}</p>
            <!-- When the subscription status is actually available here, uncomment two lines below and remove the third (currently active) line. -->
            <!-- <p v-else-if="hasCancelledSubscription" class="notice">{{$t('components.account_list_item.notices.' + (isGracePeriod ? 'cancellation_grace' : 'cancellation'), {days: daysFromNow(account.paid_to_time)})}}</p> -->
            <!-- <p v-else-if="!isFree" class="notice">{{$t('components.account_list_item.notices.' + (isGracePeriod ? 'renewal_grace' : 'renewal'), {days: daysFromNow(account.paid_to_time)})}}</p> -->
            <p v-else-if="!isFree && !cheapmium" class="notice">{{$t('components.account_list_item.notices.' + (isGracePeriod ? 'paid_to_grace' : 'paid_to'), {days: daysFromNow(account.paid_to_time)})}}</p>
        </div>
    </div>
</template>

<script>
    import { mapActions } from 'pinia';
    import { useUserStore } from '@/stores/user';
    import { useAccountStore } from '@/stores/account';
    import router from '@/router';
    import CompanyLogo from '@/components/misc/CompanyLogo';
    import i18n from "@/plugins/i18n";
    import { usePopupStore } from '@/stores/utils/popup';
    import { useToastStore } from '@/stores/utils/toast';
    import { useProductsStore } from '@/stores/products';
    import { markRaw } from 'vue';
    import AccountPartnerForm from '@/components/forms/AccountPartnerForm';
    import { timestampToDisplayDate, daysFromNow } from '@/utils/dates';

    export default {
        name: 'AccountsListItem',
        components: {
            CompanyLogo
        },
        props: {
            account: {
                type: Object,
                required: true,
            },
        },
        computed: {
            currentStatus() {
                switch (this.account.status) {
                    case -1:
                        return 'deactivated';
                    case 0:
                        return 'inactive';
                    case 1:
                    default:
                        return 'active';
                }
            },
            product() {
                return !!this.account?.product_main && useProductsStore().getProduct(this.account.product_main);
            },
            hasCancelledSubscription() {
                return this.account?.subscription?.status === 'cancelled' ? true : false;
            },
            isProspectPro() {
                return process.env.VUE_APP_TYPE === 'bedrijfsdata';
            },
            isDevelopers() {
                return process.env.VUE_APP_TYPE === 'developers';
            },
            isPaid() {
                return this.product?.price > 0;
            },
            isGracePeriod() {
                return this.account?.paid_to_time && this.account.paid_to_time * 1000 < Date.now();
            },
            isTrial() {
                return !!this.account.product_main && this.account.product_main === process.env.VUE_APP_PRODUCT_TRIAL;
            },
            isFree() {
                return !!this.account.product_main && this.account.product_main === process.env.VUE_APP_PRODUCT_FREE;
            },
            isCustom() {
                return this.account?.product?.startsWith('ma_');
            },
            isPartnerAdmin() {
                return useUserStore().isPartnerAdmin;
            },
            isPartnerGod() {
                return useUserStore().isPartnerGod;
            },
            locale() {
                return i18n.locale || 'nl';
            },
            cheapmium() {
                return this.account.product.includes('_pch');
            }
        },
        methods: {
            timestampToDisplayDate,
            daysFromNow,
            ...mapActions(useAccountStore, ['setAccount']),
            ...mapActions(useProductsStore, ['getProduct']),
            selectAccount() {
                this.setAccount({ account_id: this.account.account_id });
                router.push({ name: process.env.VUE_APP_DEFAULT_ROUTE });
            },
            accountSettings() {
                this.setAccount({ account_id: this.account.account_id });
                router.push({ name: 'settings' });
            },
            accountSubscriptions() {
                this.setAccount({ account_id: this.account.account_id });
                router.push({ name: 'subscriptions' });
            },
            editAccountPopup(account) {
                usePopupStore().showPopup('form', {
                    title: 'popups.edit_account.title',
                    description: 'popups.edit_account.description',
                    formComponent: markRaw(AccountPartnerForm),
                    formProps: {
                        account: account
                    },
                    onFormSuccess: () => {
                        useToastStore().addToast(i18n.global.t('toasts.success.account_updated'), 'success');
                    }
                });
            },
        },
    };
</script>